import { FieldMeta } from '../../../types/webchatTabsTypes';
import { SectionMeta } from './types';

export const SECTION_META: SectionMeta[] = [
  { id: 'context-history', heading: 'Context & History', subtitle: 'How much conversation history and pinned context is sent to the LLM.' },
  { id: 'summarization', heading: 'Summarization', subtitle: 'Compact long conversations while keeping recent turns available verbatim.' },
  { id: 'run-budgets', heading: 'Run & Budgets', subtitle: 'Per-turn execution limits and token budgets.' },
  { id: 'results-data', heading: 'Results & Data', subtitle: 'Storage and retrieval of tool results and prior conversation data.' },
  { id: 'reliability', heading: 'Reliability & Diagnostics', subtitle: 'Safety nets and observability for tool execution.' },
  { id: 'data-analyst', heading: 'Data Analyst', subtitle: 'Limits for the Data Analyst sub-agent (widgets with "Advanced data analysis" on).' },
];

export const FIELDS_BY_SECTION: Record<string, FieldMeta[]> = {
  'context-history': [
    {
      key: 'webchat_max_history_messages',
      label: 'Max History Messages',
      description: 'Maximum non-system LlmMessage rows sent to the LLM per provider call (not user turns).',
      details: 'Limits how many previous messages the AI can see when answering. A higher number lets it remember more of the conversation, but adds more text (and cost) to every reply. This only applies while History Summarization below is off — once summarization is on, older messages are condensed instead of being cut off.',
      type: 'number',
      fallbackDefault: 24,
      min: 1,
      step: 1,
      disabledWhen: d => d['webchat_enable_history_summarization'] === 'true',
      disabledNote: 'Disabled while History Summarization is on — older messages are condensed instead of being capped.',
    },
    {
      key: 'webchat_max_history_tokens',
      label: 'Max History Tokens',
      description: 'Estimated token budget for non-system conversation history sent to the LLM per provider call.',
      details: 'Works like Max History Messages, but limits by estimated text size instead of message count. Also only applies while History Summarization below is off.',
      type: 'number',
      fallbackDefault: 12000,
      min: 1,
      step: 500,
      disabledWhen: d => d['webchat_enable_history_summarization'] === 'true',
      disabledNote: 'Disabled while History Summarization is on — older messages are condensed instead of being capped.',
    },
    {
      key: 'webchat_enable_pinned_facts',
      label: 'Enable Pinned Facts',
      description: 'Persist pinned facts that survive history trimming.',
      details: 'When on, important facts confirmed during the conversation (like chosen values or decisions) are remembered so the assistant still knows them even after older messages are trimmed away. Turning this off stops new facts from being remembered going forward, but facts already remembered before that point will keep appearing for the rest of the conversation.',
      type: 'boolean',
      fallbackDefault: 'true',
    },
    {
      key: 'webchat_max_pinned_fact_tokens',
      label: 'Max Pinned Fact Tokens',
      description: 'Token budget for injected pinned facts in LLM context.',
      details: 'Limits how much space is reserved for these remembered facts in every message sent to the assistant. If there are too many facts to fit, the most important ones (confirmed choices and identifiers) are kept first.',
      type: 'number',
      fallbackDefault: 400,
      min: 1,
      step: 100,
      dependsOn: 'webchat_enable_pinned_facts',
    },
  ],
  summarization: [
    {
      key: 'webchat_enable_history_summarization',
      label: 'History Summarization',
      description: 'Automatically compact long conversations while keeping the latest messages available verbatim.',
      details: 'When the conversation gets long, this automatically compresses older parts into a short summary instead of sending the full text every time, while keeping the most recent messages exact. This keeps costs down and lets conversations run much longer without losing context.',
      type: 'boolean',
      fallbackDefault: 'true',
    },
    {
      key: 'webchat_history_summary_trigger_tokens',
      label: 'Trigger',
      description: 'Estimated tokens across concluded history segments that triggers a summarization pass.',
      details: 'How much older conversation needs to build up before the system creates or refreshes the summary. A lower value summarizes sooner (more compression, slightly less verbatim detail kept); a higher value waits longer before compressing.',
      type: 'number',
      fallbackDefault: 35000,
      min: 1,
      step: 1000,
      dependsOn: 'webchat_enable_history_summarization',
    },
    {
      key: 'webchat_history_keep_raw_tail_tokens',
      label: 'Keep Recent History',
      description: 'Verbatim recent history kept out of summarization, segment-aligned.',
      details: 'How much of the most recent conversation is always kept exactly as written (never summarized), so the assistant has precise detail on what was just discussed.',
      type: 'number',
      fallbackDefault: 12500,
      min: 1,
      step: 500,
      dependsOn: 'webchat_enable_history_summarization',
    },
    {
      key: 'webchat_history_summary_target_tokens',
      label: 'Target',
      description: 'Target size for the persisted rolling conversation summary after (re-)condensation.',
      details: 'The approximate size the generated summary should end up at. Smaller means a shorter, cheaper summary with less detail; larger keeps more detail but costs more.',
      type: 'number',
      fallbackDefault: 4000,
      min: 1,
      step: 500,
      dependsOn: 'webchat_enable_history_summarization',
    },
    {
      key: 'webchat_max_summary_tokens',
      label: 'Max Summary Tokens',
      description: 'Token budget for the injected conversation summary in LLM context.',
      details: 'Intended to limit the size of the summary shown to the assistant. This setting currently has no effect on behavior — changing it will not change anything.',
      type: 'number',
      fallbackDefault: 800,
      min: 1,
      step: 100,
      advanced: true,
      dependsOn: 'webchat_enable_history_summarization',
    },
    {
      key: 'webchat_history_summary_max_condense_attempts',
      label: 'Max Condense Attempts',
      description: 'Bounded re-condensation retries before an over-target summary is accepted as-is (never truncated).',
      details: 'If a generated summary comes out bigger than the Target size, the system tries to shrink it again, up to this many times, before accepting it as-is rather than cutting content.',
      type: 'number',
      fallbackDefault: 2,
      min: 1,
      step: 1,
      advanced: true,
      dependsOn: 'webchat_enable_history_summarization',
    },
  ],
  // --- Interim sections: still flat/basic pending the next redesign pass ---
  'run-budgets': [
    {
      key: 'webchat_max_client_tool_round_trips',
      label: 'Max Tool Round Trips',
      description: 'Maximum client-side tool execution round trips per user turn (one round trip = one LLM tool batch + continuation).',
      details: 'The maximum number of times, within one user question, that the assistant can call a tool and get a result back before continuing. If this limit is reached, the user is asked to start a new question — this prevents a conversation from looping endlessly.',
      type: 'number',
      fallbackDefault: 10,
      min: 1,
      step: 1,
    },
    {
      key: 'webchat_enable_parallel_tool_execution',
      label: 'Parallel Execution',
      description: 'When enabled, explicitly-confirmed read-only tool calls within one LLM turn execute concurrently instead of sequentially.',
      details: 'When on, multiple safe, read-only actions requested by the assistant in the same step run at the same time instead of one after another, making responses faster.',
      type: 'boolean',
      fallbackDefault: 'true',
    },
    {
      key: 'webchat_run_soft_token_budget',
      label: 'Soft Budget',
      description: 'Cumulative token log-level warning threshold per user turn. Never blocks or cuts anything.',
      details: 'A usage-warning threshold for a single question. Crossing it does not stop or change anything for the user — it\'s only used behind the scenes for monitoring.',
      type: 'number',
      fallbackDefault: 80000,
      min: 1,
      step: 5000,
    },
    {
      key: 'webchat_run_hard_token_budget',
      label: 'Hard Budget',
      description: 'Cumulative token threshold per user turn that stops starting new iterations, once "Hard Stop" is on. Never cuts data.',
      details: 'A usage ceiling for a single question. What actually happens when it\'s reached depends on the Hard Stop setting below.',
      type: 'number',
      fallbackDefault: 120000,
      min: 1,
      step: 5000,
    },
    {
      key: 'webchat_enable_run_hard_stop',
      label: 'Hard Stop',
      description: 'Enforcement switch for the run hard token budget. Starts disabled - observe soft-threshold logs before enabling.',
      details: 'Turns the Hard Budget above from a monitoring-only limit into one that\'s actually enforced: when on, the assistant stops working on the current question once the Hard Budget is reached, instead of continuing. This is off by default, meaning the Hard Budget currently does not stop anything on its own.',
      type: 'boolean',
      fallbackDefault: 'false',
    },
  ],
  'results-data': [
    {
      key: 'webchat_enable_history_retrieval',
      label: 'History Retrieval',
      description: 'Kill switch for the history-retrieval tools and the references block injected into context.',
      details: 'Lets the assistant fetch the full, exact text of an older message or tool result when needed, even after it has been condensed into a summary. Turning this off removes that ability entirely.',
      type: 'boolean',
      fallbackDefault: 'true',
    },
    {
      key: 'webchat_history_max_dataset_references',
      label: 'Max Dataset References',
      description: 'Maximum remembered dataset references in the rolling conversation summary; oldest dropped first.',
      details: 'How many references to data or files mentioned earlier in the conversation stay available in the summary; the oldest are dropped first once the limit is reached.',
      type: 'number',
      fallbackDefault: 20,
      min: 1,
      step: 1,
    },
    {
      key: 'webchat_history_retrieval_max_references',
      label: 'Max History References',
      description: 'Cap on how many lines the history-retrieval tools return, most-recent-first (oldest dropped).',
      details: 'How many items appear in the list of retrievable older messages/results the assistant can pull from, shown most-recent-first.',
      type: 'number',
      fallbackDefault: 30,
      min: 1,
      step: 1,
    },
  ],
  reliability: [
    {
      key: 'webchat_enable_persistent_idempotency',
      label: 'Persistent Idempotency',
      description: 'Enable database-backed idempotency for side-effecting tool calls.',
      details: 'When on, if the exact same action (like creating a record) gets accidentally requested twice, the system recognizes it and avoids doing it again — protecting against accidental duplicates, for example from a retry. Turning this off removes that protection.',
      type: 'boolean',
      fallbackDefault: 'true',
    },
    {
      key: 'webchat_enable_server_failed_call_dedup',
      label: 'Failed Call Dedup',
      description: 'Block repeated identical non-retryable failed tool calls within the same turn (server-authoritative).',
      details: 'When on, if an action fails and the assistant tries the exact same thing again within the same question, the system blocks the repeat immediately instead of letting it fail again the same way.',
      type: 'boolean',
      fallbackDefault: 'true',
    },
    {
      key: 'webchat_enable_provider_diagnostics',
      label: 'Provider Diagnostics',
      description: 'Record per-provider-call diagnostics with real usage tokens where available.',
      details: 'When on, technical details about each AI response (like token usage and cost) are recorded for monitoring and troubleshooting. Turning it off stops that recording.',
      type: 'boolean',
      fallbackDefault: 'true',
    },
  ],
  'data-analyst': [
    {
      key: 'webchat_data_analyst_checker_enabled',
      label: 'Requirements Checker',
      description: 'Before each Data_Analyst run, an LLM checks the task against the business instructions and the user request. Each widget can also turn it off.',
      details: 'When off, tasks go straight to the analyst after the table/field check (which always runs). Saves one LLM call per step, but rules the main agent left out or got wrong are no longer caught. Edit its prompt in Settings > Prompts > "Data Analyst – Requirements Checker".',
      type: 'boolean',
      fallbackDefault: 'true',
    },
    {
      key: 'webchat_data_analyst_checker_model',
      label: 'Requirements Checker Model',
      description: 'Model id for the requirements checker. Empty = same model as the chat.',
      type: 'string',
      fallbackDefault: '',
      note: 'Same model as the chat',
      dependsOn: 'webchat_data_analyst_checker_enabled',
      disabledNote: 'Disabled while the Requirements Checker is off.',
    },
    {
      key: 'webchat_data_analyst_examples_enabled',
      label: 'Reuse Accepted Queries',
      description: 'Offer the last accepted query per widget + result name as a starting point over tables with the same columns.',
      details: 'When on, the analyst starts from the query that worked last time for the same result, which usually saves rounds.',
      type: 'boolean',
      fallbackDefault: 'true',
    },
    {
      key: 'webchat_data_analyst_max_tokens_per_call',
      label: 'Token Budget per Call',
      description: 'Cumulative input + output tokens for one Data_Analyst call (cache reads not counted). Exceeding it stops the analysis as "failed".',
      details: 'Every round replays the cached business rules; those cache reads do not count here. When the budget runs out the analysis is cut off and the main agent has to send the task again, so a budget that is too low makes runs slower, not cheaper.',
      type: 'number',
      fallbackDefault: 60000,
      min: 1,
      step: 10000,
    },
    {
      key: 'webchat_data_analyst_max_iterations',
      label: 'Max Rounds',
      description: 'Maximum LLM rounds for one Data_Analyst call before it returns "failed".',
      details: 'Each round is one model reply: running queries, fixing an error, or submitting the result.',
      type: 'number',
      fallbackDefault: 8,
      min: 1,
      step: 1,
    },
    {
      key: 'webchat_data_analyst_run_sql_max_rows',
      label: 'Rows Returned per Query',
      description: 'Maximum rows returned to the analyst from each query it runs (the saved result always keeps every row).',
      details: 'The analyst sees this many rows of each result plus the total row count. Higher values make every round larger.',
      type: 'number',
      fallbackDefault: 20,
      min: 1,
      step: 5,
    },
    {
      key: 'webchat_data_analyst_sample_rows',
      label: 'Sample Rows per Table',
      description: 'Sample rows per loaded table included in the analyst prompt.',
      details: 'Gives the analyst a feel for the data before it writes SQL. Higher values make every round larger.',
      type: 'number',
      fallbackDefault: 5,
      min: 0,
      step: 1,
    },
  ],
};

export const ALL_FIELDS: FieldMeta[] = Object.values(FIELDS_BY_SECTION).flat();
